import React, { Component } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import "./App.css";
import Sidenav from "./components/sidebar/Sidenav";
import Firmware from "./pages/Firmware";
import UserManagement from "./pages/UserManagement";
import SiteDetails from "./pages/SiteDetails.js";
import ReleaseManagement from "./pages/ReleaseManagement";
import { getAPIErrorType } from "./api/axiosConfig";
import {
  onMsalLogOut,
  getMsalUserInfo,
  getMsalLogout,
  getMsalRefreshedToken,
  isUserLoggedIn,
} from "./authConfig";
import { getAPIUrl, getUserRole, checkUserAccessLevel } from "./components/common/helpers";
import axios from "axios";
import { getAuthToken } from "./api/api";
import { IdleTimer } from "./components/common/IdleTimeComponent";
import moment from "moment";
import Countdown from "react-countdown-now";
import { Modal, ModalBody, ModalHeader } from "react-bootstrap";
import { InvalidAccess } from "./components/common/InvalidAccess";
import { Spinner } from "../src/components/common/Spinner";
import styled from "styled-components";
import FirmwareReport from "./pages/FirmwareReport.js";
import ContractManagement from "./pages/ContractManagement.js";
import TracesExceptionsPage from "./pages/TracesExceptionsDashboard.js";
import EmailTraces from "./pages/EmailTraces.js";
import { getFirstAccessibleRoute } from "./components/sidebar/routeAccess";

let timerReset;

const StyledModal = styled(Modal)`
  display: block !important;
  margin-left: auto !important;
  margin-right: auto !important;
  overflow: hidden !important;

  .modal-dialog {
    width: 100% !important;
    .modal-content {
      padding: 5px;
      height: 100% !important;
      width: 100% !important;
      border-radius: unset;
      top: 150px;
      .modal-header {
        padding: 15px 15px 1px 15px;
        color: black;
      }

      .modal-body {
        height: 100% !important;
        width: 100% !important;
        color: black;
        div {
          padding: 10px 0 0 0;

          button {
            border: 1px solid black;
            color: black;
            background-color: white;
            min-width: 100px;
            width: 25%;

            :hover {
              background-color: black;
              color: white;
            }
          }
        }
      }
    }
  }
`;

const Roles = {
  ADMIN: "GA",
  RELEASEMANAGER: "RM",
  FILEMANAGER: "FM",
  VIEWER: "Viewer",
  TECHNICALSUPPORT: "TS"
}

const RouterComponents = [
  {
    path: "/usermanagement",
    component: UserManagement,
    requiredRole: [Roles.ADMIN]
  },
  {
    path: "/sitedetails",
    component: SiteDetails,
    requiredRole: [Roles.ADMIN, Roles.RELEASEMANAGER, Roles.VIEWER, Roles.TECHNICALSUPPORT]
  },
  {
    path: "/releasemanagement",
    component: ReleaseManagement,
    requiredRole: [Roles.ADMIN, Roles.RELEASEMANAGER, Roles.VIEWER, Roles.TECHNICALSUPPORT]
  },
  {
    path: "/firmwarereport",
    component: FirmwareReport,
    requiredRole: [Roles.ADMIN, Roles.RELEASEMANAGER, Roles.VIEWER, Roles.TECHNICALSUPPORT]
  },
  {
    path: "/contractmanagement",
    component: ContractManagement,
    requiredRole: [Roles.ADMIN, Roles.RELEASEMANAGER, Roles.VIEWER, Roles.TECHNICALSUPPORT]
  },
  {
    path: "/traces-exceptions/traces",
    component: TracesExceptionsPage,
    requiredRole: [
      Roles.ADMIN,
      Roles.RELEASEMANAGER,
      Roles.VIEWER,
      Roles.TECHNICALSUPPORT
    ]
  },
  {
    path: "/traces-exceptions/exceptions",
    component: TracesExceptionsPage,
    requiredRole: [
      Roles.ADMIN,
      Roles.RELEASEMANAGER,
      Roles.VIEWER,
      Roles.TECHNICALSUPPORT
    ]
  },
  {
    path: "/traces-exceptions/emailTraces",
    component: EmailTraces,
    requiredRole: [
      Roles.ADMIN,
      Roles.RELEASEMANAGER,
      Roles.VIEWER,
      Roles.TECHNICALSUPPORT
    ]
  },
  {
    path: "/", component: Firmware,
    requiredRole: [
      Roles.ADMIN,
      Roles.RELEASEMANAGER,
      Roles.VIEWER,
      Roles.FILEMANAGER,
    ]
  },
];

const ProtectedRoute = ({
  Component,
  disabledFeatures,
  roleId,
  requiredRole,
}) => {
  const location = useLocation();
  const currentRole = getUserRole(roleId);

  const hasAccess =
    !requiredRole ||
    (
      Array.isArray(requiredRole) &&
      requiredRole.includes(currentRole)
    );

  if (!hasAccess) {
    const firstAccessibleRoute =
      getFirstAccessibleRoute(roleId);

    /*
     * Redirect the user to the first sidenav route available
     * for the newly assigned role.
     */
    if (firstAccessibleRoute) {
      return (
        <Navigate
          to={firstAccessibleRoute}
          replace
          state={{
            redirectedFrom: location.pathname,
            reason: "role-access-changed",
          }}
        />
      );
    }

    /*
     * Keep InvalidAccess only as a fallback when the role has
     * no configured sidenav routes.
     */
    return <InvalidAccess level="role" />;
  }

  return (
    <Component
      disabledFeatures={disabledFeatures}
    />
  );
};

class App extends Component {
  constructor(props) {
    super(props);

    window.addEventListener("storage", (e) => onMsalLogOut());
    const isPublicRoute = window.location.pathname.startsWith("/inspection");
    moment.locale(this.props.language);

    this.handleAuthState = this.handleAuthState.bind(this);

    localStorage.setItem("Session Time", moment().unix());
    this.idleTimer = null;
    this.onIdle = this._onIdle?.bind(this);
    this.onAction = this._onAction?.bind(this);
    this.getTheConditionalRender = this.getTheConditionalRender?.bind(this);

    this.state = {
      showPopup: false,
      showProfile: false,
      userDetails: "LOADING",
      error: false,
    };
    if (!isPublicRoute) {
      window.setInterval(() => {
        getMsalRefreshedToken();
      }, 3000000);
    }


    const userInfo = getMsalUserInfo();
    const upn = userInfo?.username;
    const url = getAPIUrl("getUserDetailsByIdUrl") + upn?.toLowerCase();

    window.addEventListener("storage", (e) => onMsalLogOut());
    const AuthToken = getAuthToken();

    const getUserDetailsByID = () => {
      return axios.get(url, {
        headers: { Authorization: AuthToken }
      })
    }

    const userDetailsFetch = async () => {
      try {
        const response = await getUserDetailsByID();
        if (response) {
          if (response?.data?.length) {
            this.setState({ userDetails: response?.data[0] });
          } else {
            this.setState({ userDetails: [] });
          }
        }
      } catch (error) {
        getAPIErrorType(error);
        this.setState({ error: true });
        this.setState({ userDetails: [] });
      }
    }

    userDetailsFetch();
  }

  componentDidMount() {
    this.handleAuthState();
    window.addEventListener("pageshow", this.handleAuthState);
    window.addEventListener("popstate", this.handleAuthState);
  }

  componentWillUnmount() {
    window.removeEventListener("pageshow", this.handleAuthState);
    window.removeEventListener("popstate", this.handleAuthState);
  }

  handleAuthState() {
    const isPublicRoute = window.location.pathname.startsWith("/inspection");
    if (isPublicRoute) {
      return;
    }

    if (!isUserLoggedIn()) {
      localStorage.clear();
      sessionStorage.clear();
      window.location.replace("/");
    }
  }

  handlePopupToggle = () => {
    localStorage.setItem("Session Time", moment().unix());
    this.setState({ showPopup: false });
    clearTimeout(timerReset);
    getMsalRefreshedToken();
  };

  _onIdle(e) {
    if (moment().unix() - localStorage.getItem("Session Time") >= 2700 &&
      document.getElementsByClassName("blur-effect").length === 0) {
      this.setState({ showPopup: true });
      timerReset = setTimeout(getMsalLogout, 10000);
    } else {
      this.idleTimer.reset();
      clearTimeout(timerReset);
    }
  }

  _onAction(e) {
    localStorage.setItem("Session Time", moment().unix());
  }

  getTheConditionalRender() {
    const userInfo = getMsalUserInfo();
    const { role_id = 0 } = this.state.userDetails;
    const disabledFeatures = checkUserAccessLevel(role_id);
    if (role_id !== 0 && this.state.error === false) {
      return (
        <>
          <IdleTimer
            ref={(ref) => {
              this.idleTimer = ref;
            }}
            onIdle={this.onIdle}
            onAction={this.onAction}
            onActive={this.onActive}
            debounce={250}
            timeout={2700000}
          />
          <Sidenav userInfo={userInfo} role_id={role_id} />
          <main>
            <StyledModal
              show={this.state.showPopup}
              onHide={this.handlePopupToggle}
            >
              <ModalHeader closeButton>Session Timeout</ModalHeader>
              <hr />
              <ModalBody>
                <Countdown
                  date={Date.now() + 10000}
                  renderer={({ seconds, completed }) =>
                    completed ? (
                      <span style={{ color: "red" }}>
                        For security purposes your session has timed-out.
                        Please go to the login page to log back in
                      </span>
                    ) : (
                      <React.Fragment>
                        <span>
                          You are being timed out due to session inactivity.
                          Please choose to stay signed in or to log off.
                          Otherwise you will be logged out automatically.
                        </span>
                        <div>
                          <button type="button" onClick={this.handlePopupToggle}>
                            Stay signed in
                          </button>
                          &nbsp;&nbsp;
                          <button type="button" onClick={getMsalLogout}>
                            Logoff({seconds})
                          </button>
                        </div>
                      </React.Fragment>
                    )
                  }
                />
              </ModalBody>
            </StyledModal>
            <Routes>
              {RouterComponents.map(
                ({
                  path,
                  component: RouteComponent,
                  requiredRole,
                }) => (
                  <Route
                    key={path}
                    path={path}
                    element={
                      <ProtectedRoute
                        Component={RouteComponent}
                        disabledFeatures={disabledFeatures}
                        requiredRole={requiredRole}
                        roleId={role_id}
                      />
                    }
                  />
                )
              )}

              {/* Invalid or unknown URL */}
              <Route
                path="*"
                element={
                  <Navigate
                    to={
                      getFirstAccessibleRoute(role_id) || "/"
                    }
                    replace
                  />
                }
              />
            </Routes>
          </main>
        </>
      )
    } else {
      return (
        <InvalidAccess level="user" error={this.state.error} />
      )
    }
  }

  render() {
    return (
      <div className="App">
        {this.state.userDetails === "LOADING" && this.state.error === false ? (
          <Spinner />
        ) : this.getTheConditionalRender()}
      </div>
    );
  }
}
export default App;
